import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaCcStripe } from 'react-icons/fa';
import { branding } from '../constants/branding';
import './BusinessRegister.css';
import { ProductSelection } from '../components/common/ProductSelection';
import axios from 'axios';
import { RootState } from '../store';
import { updateBusinessForm, clearBusinessForm } from '../store/slices/registrationSlice';
import { BusinessFormData } from '../store/types/registration.types';
import { getApiBaseUrl } from '../utils/apiConfig';
import { emailVerificationService } from '../services/emailVerificationService';

// Initialize CSS variables from branding
const initializeBrandingStyles = () => {
  const root = document.documentElement;
  
  // Sidebar
  root.style.setProperty('--sidebar-width', branding.sidebar.width);
  root.style.setProperty('--sidebar-background', branding.sidebar.background);
  root.style.setProperty('--sidebar-padding', branding.sidebar.padding);
  root.style.setProperty('--sidebar-text-color', branding.sidebar.textColor);
  
  // Registration Main
  root.style.setProperty('--registration-main-padding', branding.registration.main.padding);
  root.style.setProperty('--registration-main-background', branding.registration.main.background);
  root.style.setProperty('--registration-main-max-width', branding.registration.main.maxWidth);
  
  // Stepper
  root.style.setProperty('--registration-stepper-width', branding.registration.stepper.width);
  root.style.setProperty('--registration-stepper-number-size', branding.registration.stepper.numberSize);
  root.style.setProperty('--registration-stepper-font-size', branding.registration.stepper.fontSize);
  root.style.setProperty('--registration-stepper-line-height', branding.registration.stepper.lineHeight);
  root.style.setProperty('--registration-stepper-active-color', branding.registration.stepper.activeColor);
  root.style.setProperty('--registration-stepper-completed-color', branding.registration.stepper.completedColor);
  root.style.setProperty('--registration-stepper-inactive-color', branding.registration.stepper.inactiveColor);
  root.style.setProperty('--registration-stepper-line-color', branding.registration.stepper.lineColor);
  root.style.setProperty('--registration-stepper-line-thickness', branding.registration.stepper.lineThickness);
  
  // Content
  root.style.setProperty('--registration-content-max-width', branding.registration.content.maxWidth);
  
  // Form Elements
  root.style.setProperty('--form-label-font-size', `${branding.forms.label.fontSize}px`);
  root.style.setProperty('--form-label-font-weight', branding.forms.label.fontWeight.toString());
  root.style.setProperty('--form-label-color', branding.forms.label.color);
  root.style.setProperty('--form-label-margin-bottom', `${branding.forms.label.marginBottom}px`);
  
  root.style.setProperty('--form-input-height', branding.forms.input.height);
  root.style.setProperty('--form-input-font-size', branding.forms.input.fontSize);
  root.style.setProperty('--form-input-padding', branding.forms.input.padding);
  root.style.setProperty('--form-input-border-radius', branding.forms.input.borderRadius);
  root.style.setProperty('--form-input-border-color', branding.forms.input.borderColor);
  root.style.setProperty('--form-input-background', branding.forms.input.background);
  root.style.setProperty('--form-input-focus-border-color', branding.forms.input.focusBorderColor);
  root.style.setProperty('--form-input-placeholder-color', branding.forms.input.placeholderColor);
  root.style.setProperty('--form-input-focus-shadow-color', `${branding.primaryColor}1a`); // 10% opacity
  
  // Buttons
  root.style.setProperty('--form-button-height', `${branding.forms.button.height}px`);
  root.style.setProperty('--form-button-padding', branding.forms.button.padding);
  root.style.setProperty('--form-button-font-size', `${branding.forms.button.fontSize}px`);
  root.style.setProperty('--form-button-font-weight', branding.forms.button.fontWeight.toString());
  root.style.setProperty('--form-button-border-radius', `${branding.forms.button.borderRadius}px`);
  root.style.setProperty('--form-button-disabled-opacity', branding.forms.input.disabledOpacity.toString());
  
  root.style.setProperty('--form-button-primary-background', branding.forms.button.primary.background);
  root.style.setProperty('--form-button-primary-color', branding.forms.button.primary.color);
  root.style.setProperty('--form-button-primary-hover-background', branding.forms.button.primary.hoverBackground);
  
  root.style.setProperty('--form-button-secondary-background', branding.forms.button.secondary.background);
  root.style.setProperty('--form-button-secondary-color', branding.forms.button.secondary.color);
  root.style.setProperty('--form-button-secondary-border', branding.forms.button.secondary.border);
  root.style.setProperty('--form-button-secondary-hover-background', branding.forms.button.secondary.hoverBackground);
  
  // Text Colors
  root.style.setProperty('--text-color', branding.textColor);
};

const steps = ['Product Selection', 'Business Info', 'Contact Details', 'Address', 'Banking Details', 'Additional Info', 'Verify'];
const API_BASE_URL = getApiBaseUrl();

const BusinessRegister = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stepperRef = useRef<HTMLDivElement>(null);
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED'>('PENDING');
  const [registrationPaymentMethod, setRegistrationPaymentMethod] = useState<'STRIPE' | 'CRYPTO'>('STRIPE');
  const [registrationCryptoBlockchain, setRegistrationCryptoBlockchain] = useState('ethereum');
  const [registrationCryptoSymbol, setRegistrationCryptoSymbol] = useState('USDT');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [emailVerificationMessage, setEmailVerificationMessage] = useState<string | null>(null);

  // Get form data from Redux and ensure all required properties exist
  const reduxData = useSelector((state: RootState) => state.registration.business.data);
  
  const form = {
    productId: reduxData?.productId || 0,
    productType: reduxData?.productType || '',
    registrationFee: reduxData?.registrationFee || 0,
    registrationFeeType: reduxData?.registrationFeeType || 'FIXED_ONE_OFF',
    name: reduxData?.name || '',
    external_identifier: reduxData?.external_identifier || null,
    tax_information: {
      tax_country: reduxData?.tax_information?.tax_country || '',
      registration_number: reduxData?.tax_information?.registration_number || '',
      registration_type: reduxData?.tax_information?.registration_type || '',
      tax_identification_number: reduxData?.tax_information?.tax_identification_number || '',
    },
    vat_number: reduxData?.vat_number || '',
    foundation_date: reduxData?.foundation_date || '',
    telephone: reduxData?.telephone || '',
    email: reduxData?.email || '',
    website: reduxData?.website || '',
    contact_details: {
      first_name: reduxData?.contact_details?.first_name || '',
      last_name: reduxData?.contact_details?.last_name || '',
      position_held: reduxData?.contact_details?.position_held || '',
      country_code: reduxData?.contact_details?.country_code || '',
      mobile_no: reduxData?.contact_details?.mobile_no || '',
      email: reduxData?.contact_details?.email || '',
    },
    registered_address: {
      line_1: reduxData?.registered_address?.line_1 || '',
      line_2: reduxData?.registered_address?.line_2 || '',
      city: reduxData?.registered_address?.city || '',
      country: reduxData?.registered_address?.country || '',
      postal_code: reduxData?.registered_address?.postal_code || '',
      locality: reduxData?.registered_address?.locality || '',
    },
    operational_address: {
      line_1: reduxData?.operational_address?.line_1 || '',
      line_2: reduxData?.operational_address?.line_2 || '',
      city: reduxData?.operational_address?.city || '',
      country: reduxData?.operational_address?.country || '',
      postal_code: reduxData?.operational_address?.postal_code || '',
      locality: reduxData?.operational_address?.locality || '',
    },
    iban4u_payment_account: {
      accepting_third_party_funds: reduxData?.iban4u_payment_account?.accepting_third_party_funds || 'NO' as const,
      estimated_monthly_transactions: reduxData?.iban4u_payment_account?.estimated_monthly_transactions || 0,
      average_amount_transaction_euro: reduxData?.iban4u_payment_account?.average_amount_transaction_euro || 0,
      held_accounts: reduxData?.iban4u_payment_account?.held_accounts || '',
    },
    business_details: {
      business_purpose: reduxData?.business_details?.business_purpose || '',
      number_employees: reduxData?.business_details?.number_employees || 0,
      share_capital: reduxData?.business_details?.share_capital || 0,
      previous_year_turnover: reduxData?.business_details?.previous_year_turnover || '',
      terms_and_conditions: reduxData?.business_details?.terms_and_conditions || false,
      privacy_accepted: reduxData?.business_details?.privacy_accepted || false,
    },
  } as BusinessFormData;
  const selectedRegistrationFee = Number(reduxData?.registrationFee || 0);
  const selectedRegistrationFeeType = (reduxData?.registrationFeeType || 'FIXED_ONE_OFF') as 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
  const selectedRegistrationPaymentMethods = (reduxData?.registrationPaymentMethods || 'ALL') as 'ALL' | 'STRIPE' | 'CRYPTO';
  const requiresPayment = selectedRegistrationFeeType !== 'NO_FEE';
  const allowedPaymentMethods: Array<'STRIPE' | 'CRYPTO'> =
    selectedRegistrationPaymentMethods === 'STRIPE'
      ? ['STRIPE']
      : selectedRegistrationPaymentMethods === 'CRYPTO'
        ? ['CRYPTO']
        : ['STRIPE', 'CRYPTO'];

  useEffect(() => {
    initializeBrandingStyles();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      if (isMobileView) {
        scrollToCurrentStep();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    scrollToCurrentStep();
  }, [activeStep, isMobile]);

  useEffect(() => {
    setEmailVerified(false);
    setEmailVerificationSent(false);
    setEmailVerificationMessage(null);
  }, [form.contact_details.email]);

  useEffect(() => {
    if (!allowedPaymentMethods.includes(registrationPaymentMethod)) {
      setRegistrationPaymentMethod(allowedPaymentMethods[0] || 'STRIPE');
    }
  }, [selectedRegistrationPaymentMethods]);

  const sendVerificationLink = async () => {
    if (!form.contact_details.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      setEmailVerificationMessage(null);
      const result = await emailVerificationService.sendLink(form.contact_details.email);
      setEmailVerificationSent(true);
      setEmailVerificationMessage(result.message || 'Verification link sent.');
    } catch (err: any) {
      setEmailVerificationMessage(
        err?.response?.data?.errors?.[0]
          || err?.response?.data?.message
          || 'Failed to send verification link.'
      );
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const refreshEmailVerificationStatus = async () => {
    if (!form.contact_details.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      const status = await emailVerificationService.getStatus(form.contact_details.email);
      setEmailVerified(Boolean(status.verified));
      setEmailVerificationMessage(status.verified
        ? 'Email verified. You can continue.'
        : 'Email not verified yet. Click the verification link and check again.'
      );
    } catch {
      setEmailVerificationMessage('Failed to check verification status.');
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment_result');
    const sessionId = params.get('session_id');
    if (!paymentResult) return;

    const finalize = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveStep(steps.length - 1);
    };

    if (paymentResult === 'cancelled') {
      setPaymentStatus('EXPIRED');
      setPaymentCompleted(false);
      setError('Payment was cancelled. You can try again.');
      finalize();
      return;
    }

    if (paymentResult === 'success' && sessionId) {
      const checkStatus = async () => {
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
          const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
          setPaymentSessionId(sessionId);
          setPaymentStatus(status);
          setPaymentCompleted(status === 'COMPLETED');
          if (status !== 'COMPLETED') {
            setError('');
            startPaymentStatusPolling(sessionId);
          }
        } catch {
          setError('Unable to verify payment status right now. Please try again.');
        } finally {
          finalize();
        }
      };
      checkStatus();
    }
  }, []);

  const scrollToCurrentStep = () => {
    if (isMobile && stepperRef.current && stepContainerRef.current) {
      const stepElements = stepContainerRef.current.querySelectorAll('.step');
      if (stepElements[activeStep]) {
        const stepElement = stepElements[activeStep] as HTMLElement;
        const stepperRect = stepperRef.current.getBoundingClientRect();
        const stepRect = stepElement.getBoundingClientRect();
        
        const scrollPosition = stepElement.offsetLeft - (stepperRect.width / 2) + (stepRect.width / 2);
        
        stepperRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleChange = (field: string, value: any) => {
    const fields = field.split('.');
    let updatedData: Partial<BusinessFormData>;
    
    if (fields.length === 1) {
      updatedData = { [field]: value };
    } else {
      const section = fields[0] as keyof BusinessFormData;
      updatedData = {
        [section]: {
          ...(form[section] as any),
          [fields[1]]: value
        }
      };
    }
    
    dispatch(updateBusinessForm(updatedData));
  };

  const isStepValidFor = (step: number) => {
    switch (step) {
      case 0: // Product Selection
        return form.productId > 0 && form.productType !== '';
      case 1: // Business Info
        return (
          form.name.trim() !== '' &&
          form.tax_information.tax_country.trim() !== '' &&
          form.tax_information.registration_number.trim() !== '' &&
          form.tax_information.registration_type.trim() !== '' &&
          form.vat_number.trim() !== ''
        );
      case 2: // Contact Details
        return (
          form.contact_details.first_name.trim() !== '' &&
          form.contact_details.last_name.trim() !== '' &&
          form.contact_details.position_held.trim() !== '' &&
          form.contact_details.mobile_no.trim() !== '' &&
          form.contact_details.email.trim() !== '' &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_details.email) &&
          emailVerified
        );
      case 3: // Address
        return (
          form.registered_address.line_1.trim() !== '' &&
          form.registered_address.city.trim() !== '' &&
          form.registered_address.country.trim() !== '' &&
          form.registered_address.postal_code.trim() !== ''
        );
      case 4: // Banking Details
        return (
          form.iban4u_payment_account.estimated_monthly_transactions > 0 &&
          form.iban4u_payment_account.average_amount_transaction_euro > 0
        );
      case 5: // Additional Info
        return (
          form.business_details.business_purpose.trim() !== '' &&
          form.business_details.number_employees > 0 &&
          form.business_details.share_capital > 0 &&
          form.business_details.previous_year_turnover.trim() !== ''
        );
      case 6: // Verify
        return (
          form.business_details.terms_and_conditions &&
          form.business_details.privacy_accepted
        );
      default:
        return false;
    }
  };

  const getFirstInvalidStep = (): number => {
    for (let step = 0; step < steps.length; step++) {
      if (!isStepValidFor(step)) return step;
    }
    return -1;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activeStep === steps.length - 1) {
      const firstInvalidStep = getFirstInvalidStep();
      if (firstInvalidStep !== -1) {
        setActiveStep(firstInvalidStep);
        setError(
          firstInvalidStep === 2 && !emailVerified
            ? 'Please verify your contact email before submitting.'
            : 'Please complete all required fields before submitting registration.'
        );
        return;
      }
      if (requiresPayment && !paymentCompleted) {
        setError('Please complete the registration payment before continuing.');
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch('/api/business/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(form),
        });

        if (!response.ok) {
          throw new Error('Registration failed');
        }

        const data = await response.json();
        // Clear form data after successful registration
        dispatch(clearBusinessForm());
        navigate('/login', { 
          state: { 
            message: 'Business registration successful! Please check your email for verification.',
            type: 'success',
            registrationId: data.id
          }
        });
      } catch (error) {
        setError('Registration failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleRegistrationPayment = () => {
    setError(null);
    if (!form.productId || selectedRegistrationFee <= 0) {
      setError('Please select a tier with a valid registration fee before starting payment.');
      return;
    }
    if (registrationPaymentMethod === 'CRYPTO') {
      setError('Crypto payment for business registration is not enabled yet. Please use Stripe for now.');
      return;
    }
    const startPayment = async () => {
      const response = await axios.post(`${API_BASE_URL}/api/public/registration-payments/create-session`, {
        registrationType: 'BUSINESS',
        email: form.email || form.contact_details.email,
        productId: form.productId,
        currency: 'EUR',
        paymentMethod: registrationPaymentMethod,
        cryptoBlockchain: registrationCryptoBlockchain,
        cryptoSymbol: registrationCryptoSymbol
      });
      if (!response.data?.success || !response.data?.data?.checkoutUrl || !response.data?.data?.sessionId) {
        setError(response.data?.error || 'Failed to create payment session.');
        return;
      }
      const sessionId = response.data.data.sessionId as string;
      setPaymentSessionId(sessionId);
      setPaymentStatus('PROCESSING');
      window.open(response.data.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      startPaymentStatusPolling(sessionId);
    };
    startPayment().catch((err: any) => setError(err?.response?.data?.error || err?.message || 'Failed to start payment.'));
  };

  const startPaymentStatusPolling = (sessionId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
        const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
        setPaymentStatus(status);
        if (status === 'COMPLETED') {
          setPaymentCompleted(true);
          clearInterval(interval);
        }
        if (status === 'FAILED' || status === 'EXPIRED') {
          setPaymentCompleted(false);
          clearInterval(interval);
        }
      } catch {
        // keep polling
      }
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 5000);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const isStepValid = () => isStepValidFor(activeStep);

  const getInputStyle = () => ({
    height: branding.forms.input.height,
    fontSize: branding.forms.input.fontSize,
    padding: branding.forms.input.padding,
    borderRadius: branding.forms.input.borderRadius,
    border: `1px solid ${branding.forms.input.borderColor}`,
    background: branding.forms.input.background,
    width: '100%',
    color: branding.textColor,
  });

  const getLabelStyle = () => ({
    fontSize: branding.forms.label.fontSize,
    fontWeight: branding.forms.label.fontWeight,
    color: branding.forms.label.color,
    marginBottom: branding.forms.label.marginBottom,
    display: 'block'
  });

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Product Selection
        return (
          <ProductSelection
            customerType="BUSINESS"
            onProductSelect={(productId, productType, registrationFee, registrationFeeType, _requiresVerification, registrationPaymentMethods) => {
              handleChange('productId', productId);
              handleChange('productType', productType);
              handleChange('registrationFee', Number(registrationFee || 0));
              handleChange('registrationFeeType', registrationFeeType || 'FIXED_ONE_OFF');
              handleChange('registrationPaymentMethods', registrationPaymentMethods || 'ALL');
              setPaymentCompleted(registrationFeeType === 'NO_FEE');
              setPaymentStatus(registrationFeeType === 'NO_FEE' ? 'COMPLETED' : 'PENDING');
            }}
            selectedProductId={form.productId}
          />
        );
      case 1: // Business Info
        return (
          <div className="form-content">
            <div className="form-group">
              <label className="form-label">Business Name</label>
              <input
                type="text"
                className="form-input"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter business name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">VAT Number</label>
              <input
                type="text"
                className="form-input"
                value={form.vat_number}
                onChange={(e) => handleChange('vat_number', e.target.value)}
                placeholder="Enter VAT number"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Foundation Date</label>
              <input
                type="date"
                className="form-input"
                value={form.foundation_date}
                onChange={(e) => handleChange('foundation_date', e.target.value)}
                placeholder="dd/mm/yyyy"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tax Country</label>
              <input
                type="text"
                className="form-input"
                value={form.tax_information.tax_country}
                onChange={(e) => handleChange('tax_information.tax_country', e.target.value)}
                placeholder="Enter tax country"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Registration Number</label>
              <input
                type="text"
                className="form-input"
                value={form.tax_information.registration_number}
                onChange={(e) => handleChange('tax_information.registration_number', e.target.value)}
                placeholder="Enter registration number"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Registration Type</label>
              <select
                className="form-input"
                value={form.tax_information.registration_type}
                onChange={(e) => handleChange('tax_information.registration_type', e.target.value)}
              >
                <option value="">Select Registration Type</option>
                <option value="LIMITED">Limited</option>
                <option value="PARTNERSHIP">Partnership</option>
                <option value="SOLE_TRADER">Sole Trader</option>
              </select>
            </div>
          </div>
        );

      case 2: // Contact Details
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className="form-input"
                value={form.contact_details.first_name}
                onChange={(e) => handleChange('contact_details.first_name', e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                value={form.contact_details.last_name}
                onChange={(e) => handleChange('contact_details.last_name', e.target.value)}
                placeholder="Enter last name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Position Held</label>
              <input
                type="text"
                className="form-input"
                value={form.contact_details.position_held}
                onChange={(e) => handleChange('contact_details.position_held', e.target.value)}
                placeholder="Enter position"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Country Code</label>
              <input
                type="text"
                className="form-input"
                value={form.contact_details.country_code}
                onChange={(e) => handleChange('contact_details.country_code', e.target.value)}
                placeholder="Enter country code"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              <input
                type="tel"
                className="form-input"
                value={form.contact_details.mobile_no}
                onChange={(e) => handleChange('contact_details.mobile_no', e.target.value)}
                placeholder="Enter mobile number"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={form.contact_details.email}
                onChange={(e) => handleChange('contact_details.email', e.target.value)}
                placeholder="Enter email address"
              />
              <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={sendVerificationLink}
                  disabled={emailVerificationLoading || !form.contact_details.email}
                  style={{
                    ...getInputStyle(),
                    width: 'auto',
                    height: 40,
                    padding: '0 14px',
                    cursor: emailVerificationLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {emailVerificationLoading ? 'Sending...' : 'Send verification link'}
                </button>
                {emailVerificationSent && (
                  <button
                    type="button"
                    onClick={refreshEmailVerificationStatus}
                    disabled={emailVerificationLoading}
                    style={{
                      ...getInputStyle(),
                      width: 'auto',
                      height: 40,
                      padding: '0 14px',
                      cursor: emailVerificationLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    I verified, check status
                  </button>
                )}
              </div>
              {(emailVerificationMessage || emailVerified) && (
                <p style={{ marginTop: 8, marginBottom: 0, color: emailVerified ? '#16a34a' : '#475569', fontSize: 13 }}>
                  {emailVerified ? 'Email verified. You can continue.' : emailVerificationMessage}
                </p>
              )}
            </div>
          </div>
        );

      case 3: // Address
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group full-width">
              <h3>Registered Address</h3>
            </div>
            <div className="form-group full-width">
              <label className="form-label">Address Line 1</label>
              <input
                type="text"
                className="form-input"
                value={form.registered_address.line_1}
                onChange={(e) => handleChange('registered_address.line_1', e.target.value)}
                placeholder="Enter address line 1"
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-input"
                value={form.registered_address.line_2}
                onChange={(e) => handleChange('registered_address.line_2', e.target.value)}
                placeholder="Enter address line 2"
              />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={form.registered_address.city}
                onChange={(e) => handleChange('registered_address.city', e.target.value)}
                placeholder="Enter city"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input
                type="text"
                className="form-input"
                value={form.registered_address.country}
                onChange={(e) => handleChange('registered_address.country', e.target.value)}
                placeholder="Enter country"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input
                type="text"
                className="form-input"
                value={form.registered_address.postal_code}
                onChange={(e) => handleChange('registered_address.postal_code', e.target.value)}
                placeholder="Enter postal code"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Locality</label>
              <input
                type="text"
                className="form-input"
                value={form.registered_address.locality}
                onChange={(e) => handleChange('registered_address.locality', e.target.value)}
                placeholder="Enter locality"
              />
            </div>
          </div>
        );

      case 4: // Banking Details
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group">
              <label className="form-label">Accept Third Party Funds?</label>
              <select
                className="form-input"
                value={form.iban4u_payment_account.accepting_third_party_funds}
                onChange={(e) => handleChange('iban4u_payment_account.accepting_third_party_funds', e.target.value as 'YES' | 'NO')}
              >
                <option value="YES">Yes</option>
                <option value="NO">No</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Estimated Monthly Transactions</label>
              <input
                type="number"
                className="form-input"
                value={form.iban4u_payment_account.estimated_monthly_transactions}
                onChange={(e) => handleChange('iban4u_payment_account.estimated_monthly_transactions', Number(e.target.value))}
                placeholder="Enter estimated monthly transactions"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Average Transaction Amount (EUR)</label>
              <input
                type="number"
                className="form-input"
                value={form.iban4u_payment_account.average_amount_transaction_euro}
                onChange={(e) => handleChange('iban4u_payment_account.average_amount_transaction_euro', Number(e.target.value))}
                placeholder="Enter average transaction amount"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Held Accounts</label>
              <input
                type="text"
                className="form-input"
                value={form.iban4u_payment_account.held_accounts}
                onChange={(e) => handleChange('iban4u_payment_account.held_accounts', e.target.value)}
                placeholder="Enter held accounts"
              />
            </div>
          </div>
        );

      case 5: // Additional Info
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group">
              <label className="form-label">Business Purpose</label>
              <input
                type="text"
                className="form-input"
                value={form.business_details.business_purpose}
                onChange={(e) => handleChange('business_details.business_purpose', e.target.value)}
                placeholder="Enter business purpose"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Number of Employees</label>
              <input
                type="number"
                className="form-input"
                value={form.business_details.number_employees}
                onChange={(e) => handleChange('business_details.number_employees', Number(e.target.value))}
                placeholder="Enter number of employees"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Share Capital</label>
              <input
                type="number"
                className="form-input"
                value={form.business_details.share_capital}
                onChange={(e) => handleChange('business_details.share_capital', Number(e.target.value))}
                placeholder="Enter share capital"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Previous Year Turnover</label>
              <input
                type="text"
                className="form-input"
                value={form.business_details.previous_year_turnover}
                onChange={(e) => handleChange('business_details.previous_year_turnover', e.target.value)}
                placeholder="Enter previous year turnover"
              />
            </div>
          </div>
        );

      case 6: // Verify
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div className="review-section">
              <h3 style={{
                fontSize: branding.forms.label.fontSize,
                fontWeight: branding.forms.label.fontWeight,
                color: branding.forms.label.color,
                marginBottom: '16px'
              }}>
                Review Your Information
              </h3>
              <div className="review-grid">
                <div className="review-item">
                  <strong>Business Name:</strong>
                  <span>{form.name}</span>
                </div>
                <div className="review-item">
                  <strong>VAT Number:</strong>
                  <span>{form.vat_number}</span>
                </div>
                <div className="review-item">
                  <strong>Contact:</strong>
                  <span>{form.contact_details.first_name} {form.contact_details.last_name}</span>
                </div>
                <div className="review-item">
                  <strong>Position:</strong>
                  <span>{form.contact_details.position_held}</span>
                </div>
                <div className="review-item full-width">
                  <strong>Registered Address:</strong>
                  <span>
                    {form.registered_address.line_1}, {form.registered_address.city}, {form.registered_address.country}
                  </span>
                </div>
                <div className="review-item full-width">
                  <strong>Business Purpose:</strong>
                  <span>{form.business_details.business_purpose}</span>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <input
                  type="checkbox"
                  style={{ marginTop: '4px' }}
                  checked={form.business_details.terms_and_conditions}
                  onChange={(e) => handleChange('business_details.terms_and_conditions', e.target.checked)}
                />
                <label className="form-label" style={{ marginBottom: 0 }}>
                  I agree to the Terms and Conditions
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <input
                  type="checkbox"
                  style={{ marginTop: '4px' }}
                  checked={form.business_details.privacy_accepted}
                  onChange={(e) => handleChange('business_details.privacy_accepted', e.target.checked)}
                />
                <label className="form-label" style={{ marginBottom: 0 }}>
                  I agree to the Privacy Policy
                </label>
              </div>
            </div>

            <div style={{
              padding: '20px',
              background: '#f8fafc',
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: 8 }}>Registration Payment</h4>
              <p style={{ marginTop: 0, color: '#475569' }}>
                {selectedRegistrationFeeType === 'NO_FEE' && 'No registration payment is required for your selected tier.'}
                {selectedRegistrationFeeType === 'FIXED_ONE_OFF' && <>A one-time registration fee of <strong>EUR {selectedRegistrationFee.toFixed(2)}</strong> for your selected tier is required before account creation.</>}
                {selectedRegistrationFeeType === 'RECURRING_MONTHLY' && <>A recurring monthly fee of <strong>EUR {selectedRegistrationFee.toFixed(2)}</strong> for your selected tier is required before account creation.</>}
              </p>
              {requiresPayment && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-input"
                      value={registrationPaymentMethod}
                      onChange={(e) => setRegistrationPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
                    >
                      {allowedPaymentMethods.includes('STRIPE') && <option value="STRIPE">Card (Stripe)</option>}
                      {allowedPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto</option>}
                    </select>
                  </div>
                  {registrationPaymentMethod === 'CRYPTO' && (
                    <>
                      <div>
                        <label className="form-label">Crypto Network</label>
                        <select
                          className="form-input"
                          value={registrationCryptoBlockchain}
                          onChange={(e) => setRegistrationCryptoBlockchain(e.target.value)}
                        >
                          <option value="ethereum">Ethereum</option>
                          <option value="polygon">Polygon</option>
                          <option value="bitcoin">Bitcoin</option>
                          <option value="stellar">Stellar</option>
                          <option value="solana">Solana</option>
                          <option value="cardano">Cardano</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Symbol</label>
                        <input
                          type="text"
                          className="form-input"
                          value={registrationCryptoSymbol}
                          onChange={(e) => setRegistrationCryptoSymbol(e.target.value.toUpperCase())}
                          placeholder="USDT"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {requiresPayment && (
                  <button
                    type="button"
                    onClick={handleRegistrationPayment}
                    style={{
                      height: branding.forms.button.height,
                      padding: branding.forms.button.padding,
                      fontSize: branding.forms.button.fontSize,
                      fontWeight: branding.forms.button.fontWeight,
                      borderRadius: branding.forms.button.borderRadius,
                      background: branding.forms.button.primary.background,
                      color: branding.forms.button.primary.color,
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      {registrationPaymentMethod === 'STRIPE' ? <FaCcStripe /> : '₿'}
                      {registrationPaymentMethod === 'STRIPE'
                        ? (selectedRegistrationFeeType === 'RECURRING_MONTHLY' ? 'Start Monthly Subscription' : 'Pay Registration Fee')
                        : 'Pay with Crypto'}
                    </span>
                  </button>
                )}
                <span style={{ color: paymentCompleted ? '#16a34a' : '#475569' }}>
                  Status: <strong>{paymentStatus}</strong>
                </span>
              </div>
              {paymentSessionId && <p style={{ marginBottom: 0, fontSize: 12, color: '#64748b' }}>Session: {paymentSessionId}</p>}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="business-register-container">
      {/* Sidebar */}
      <div className="business-register-sidebar">
        <div>
          <div style={{
            fontSize: branding.sidebar.logo.fontSize,
            fontWeight: branding.sidebar.logo.fontWeight,
            marginBottom: branding.sidebar.logo.marginBottom,
            color: branding.sidebar.logo.color
          }}>
            {branding.sidebar.logoText}
          </div>
          <h1 style={{
            fontSize: branding.sidebar.title.fontSize,
            fontWeight: branding.sidebar.title.fontWeight,
            marginBottom: branding.sidebar.title.marginBottom,
            color: branding.sidebar.title.color
          }}>
            Start Your Business Banking Journey
          </h1>
          <p style={{
            fontSize: branding.sidebar.text.fontSize,
            lineHeight: branding.sidebar.text.lineHeight,
            color: branding.sidebar.text.color
          }}>
            Join thousands of businesses who trust us with their banking needs. 
            Experience enterprise-grade banking with dedicated support and powerful tools.
          </p>
        </div>
        <div style={{
          fontSize: branding.sidebar.footer.fontSize,
          color: branding.sidebar.footer.color
        }}>
          © {new Date().getFullYear()} {branding.sidebar.logoText}. All rights reserved.
        </div>
      </div>

      {/* Main Content */}
      <div className="business-register-main">
        {/* Stepper */}
        <div className="stepper" ref={stepperRef}>
          <div ref={stepContainerRef} style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 'fit-content',
            position: 'relative',
            padding: '0 16px'
          }}>
            {steps.map((label, index) => (
              <div key={label} className={`step ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'completed' : ''}`}>
                <div className="step-number">
                  {index < activeStep ? '✓' : index + 1}
                </div>
                <div className="step-label">{label}</div>
                {index < steps.length - 1 && <div className="step-line" />}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="form-container">
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              {steps[activeStep]}
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0
            }}>
              {activeStep === 0 && "Select a product tier to continue"}
              {activeStep === 1 && "Please provide your basic business information"}
              {activeStep === 2 && "Enter your contact details"}
              {activeStep === 3 && "Enter your address information"}
              {activeStep === 4 && "Enter your banking details"}
              {activeStep === 5 && "Tell us more about your business"}
              {activeStep === 6 && "Review and confirm your information"}
            </p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {renderStepContent()}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '16px',
              marginTop: '40px'
            }}>
              {activeStep > 0 && (
                <button
                  type="button"
                  style={{
                    height: branding.forms.button.height,
                    padding: branding.forms.button.padding,
                    fontSize: branding.forms.button.fontSize,
                    fontWeight: branding.forms.button.fontWeight,
                    borderRadius: branding.forms.button.borderRadius,
                    background: branding.forms.button.secondary.background,
                    color: branding.forms.button.secondary.color,
                    border: `1px solid ${branding.forms.button.secondary.border}`,
                    cursor: 'pointer',
                    opacity: isLoading ? branding.forms.input.disabledOpacity : 1
                  }}
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                style={{
                  height: branding.forms.button.height,
                  padding: branding.forms.button.padding,
                  fontSize: branding.forms.button.fontSize,
                  fontWeight: branding.forms.button.fontWeight,
                  borderRadius: branding.forms.button.borderRadius,
                  background: branding.forms.button.primary.background,
                  color: branding.forms.button.primary.color,
                  border: 'none',
                  cursor: 'pointer',
                  opacity: (isLoading || !isStepValid()) ? branding.forms.input.disabledOpacity : 1
                }}
                disabled={isLoading || !isStepValid()}
              >
                {isLoading ? 'Loading...' : activeStep === steps.length - 1 ? 'Complete Registration' : 'Continue'}
              </button>
            </div>
          </form>

          <div style={{
            textAlign: 'center',
            marginTop: '32px',
            color: branding.forms.label.color,
            fontSize: branding.forms.input.fontSize
          }}>
            Already have an account?
            <Link to="/login" style={{
              color: branding.primaryColor,
              textDecoration: 'none',
              fontWeight: branding.forms.button.fontWeight,
              marginLeft: '4px'
            }}>
              Sign in here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessRegister;